import { ApiClient, ApiError } from "./client";
import type {
  Annotations,
  AnnotationsResponse,
  PresignFrameResponse,
  PutAnnotationsResponse,
} from "./types";

export type PutResult =
  | { kind: "ok"; etag: string }
  | { kind: "conflict" }
  | { kind: "error"; status: number; message: string };

export class AnnotationsApi {
  constructor(private readonly client: ApiClient) {}

  private get baseUrl(): string {
    // ApiClient holds baseUrl privately; we read it once via a known endpoint.
    // Simpler: depend on the public client.
    return (this.client as unknown as { opts: { baseUrl: string } }).opts.baseUrl;
  }

  private withTeam(path: string, team?: string): string {
    if (team === undefined) return path;
    return path.includes("?")
      ? `${path}&team=${encodeURIComponent(team)}`
      : `${path}?team=${encodeURIComponent(team)}`;
  }

  async get(flightId: string, team?: string): Promise<AnnotationsResponse> {
    const res = await this.client.rawRequest(
      this.withTeam(`/api/flights/${encodeURIComponent(flightId)}/annotations`, team),
    );
    if (!res.ok) {
      throw new ApiError(res.status, `http_${res.status}`, await res.text());
    }
    return (await res.json()) as AnnotationsResponse;
  }

  async put(
    flightId: string,
    body: Annotations,
    ifMatchEtag: string | null,
    team?: string,
  ): Promise<PutResult> {
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (ifMatchEtag === null) headers["If-None-Match"] = "*";
    else headers["If-Match"] = ifMatchEtag;

    try {
      const res = await this.client.rawRequest(
        this.withTeam(`/api/flights/${encodeURIComponent(flightId)}/annotations`, team),
        { method: "PUT", headers, body: JSON.stringify({ annotations: body }) },
      );
      if (res.status === 412) return { kind: "conflict" };
      if (!res.ok) {
        return { kind: "error", status: res.status, message: await res.text() };
      }
      const parsed = (await res.json()) as PutAnnotationsResponse;
      return { kind: "ok", etag: parsed.etag };
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      const message = (err as Error).message ?? "request failed";
      return { kind: "error", status, message };
    }
  }

  async presignFrame(
    flightId: string,
    frameId: string,
    kind: "full" | "thumb",
    contentType: string,
    contentLength: number,
    team?: string,
  ): Promise<PresignFrameResponse> {
    const res = await this.client.rawRequest(
      this.withTeam(
        `/api/flights/${encodeURIComponent(flightId)}/frames/${encodeURIComponent(frameId)}/presign`,
        team,
      ),
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ kind, contentType, contentLength }),
      },
    );
    if (!res.ok) throw new ApiError(res.status, "presign_failed", await res.text());
    return (await res.json()) as PresignFrameResponse;
  }

  async uploadFrame(presignedUrl: string, blob: Blob, contentType: string): Promise<void> {
    const res = await fetch(presignedUrl, {
      method: "PUT",
      headers: { "content-type": contentType },
      body: blob,
    });
    if (!res.ok) throw new ApiError(res.status, "upload_failed", await res.text());
  }

  async frameUrl(
    flightId: string,
    frameId: string,
    variant: "full" | "thumb",
    team?: string,
  ): Promise<string> {
    const res = await this.client.rawRequest(
      this.withTeam(
        `/api/flights/${encodeURIComponent(flightId)}/frames/${encodeURIComponent(frameId)}?variant=${variant}`,
        team,
      ),
    );
    if (!res.ok) throw new ApiError(res.status, "frame_url_failed", await res.text());
    const body = (await res.json()) as { url: string; expiresAt: string };
    return body.url;
  }

  async deleteFrame(
    flightId: string,
    frameId: string,
    team?: string,
    reviewerId?: string,
  ): Promise<void> {
    const headers: Record<string, string> = {};
    if (reviewerId !== undefined) headers["X-Reviewer-Id"] = reviewerId;
    const res = await this.client.rawRequest(
      this.withTeam(
        `/api/flights/${encodeURIComponent(flightId)}/frames/${encodeURIComponent(frameId)}`,
        team,
      ),
      { method: "DELETE", headers },
    );
    if (!res.ok && res.status !== 404) {
      throw new ApiError(res.status, "delete_failed", await res.text());
    }
  }
}
